import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Star,
  Loader2, AlertCircle, Search, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCourses() {
  const { authHeaders } = useAdminAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/courses`, { headers: authHeaders });
      setCourses(res.data);
    } catch (e) {
      setError('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/courses/${id}`, { headers: authHeaders });
      setCourses(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggle = async (course, field) => {
    const updated = { ...course, [field]: !course[field] };
    try {
      await axios.put(`${API}/admin/courses/${course.id}`, updated, { headers: authHeaders });
      setCourses(prev => prev.map(c => c.id === course.id ? updated : c));
    } catch (e) {
      alert('Update failed.');
    }
  };

  const filtered = courses.filter(c =>
    !search || c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-6xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Courses</h1>
            <p className="text-gray-500 text-sm">{courses.length} courses in catalogue</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/courses/bulk-upload"
              className="flex items-center gap-2 border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            >
              <Upload size={15} /> Bulk Upload
            </Link>
            <Link
              to="/admin/courses/new"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            >
              <Plus size={16} /> Add Course
            </Link>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search courses…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-400 text-sm">No courses found.</p>
            <Link to="/admin/courses/new" className="mt-3 inline-flex items-center gap-1.5 text-blue-600 hover:underline text-sm">
              <Plus size={14} /> Add the first course
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Course</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden sm:table-cell">Level</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Price</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden md:table-cell">Featured</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden sm:table-cell">Active</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(course => (
                  <tr key={course.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {course.image && (
                          <img src={course.image} alt="" className="w-10 h-7 rounded object-cover shrink-0 hidden sm:block" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate max-w-[200px]">{course.title}</p>
                          <p className="text-xs text-gray-400">{course.awarding_body}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{course.category}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs hidden sm:table-cell">{course.level}</td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-700">£{course.price}</span>
                      {course.on_sale && course.original_price && (
                        <span className="text-gray-400 line-through text-xs ml-1">£{course.original_price}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <button onClick={() => handleToggle(course, 'featured')} className="text-gray-400 hover:text-blue-600 transition-colors">
                        {course.featured
                          ? <Star size={16} className="text-amber-400" fill="currentColor" />
                          : <Star size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      <button onClick={() => handleToggle(course, 'active')} className="text-gray-400 hover:text-blue-600 transition-colors">
                        {course.active
                          ? <ToggleRight size={20} className="text-green-500" />
                          : <ToggleLeft size={20} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/admin/courses/edit/${course.id}`}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Pencil size={14} />
                        </Link>
                        <button
                          onClick={() => handleDelete(course.id, course.title)}
                          disabled={deleting === course.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deleting === course.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
